import React, { useEffect, useState, useMemo } from 'react';
import Card from 'components/card';
import { useParams, useNavigate } from 'react-router-dom';
import { getPurchaseBySupplierToken, acceptPurchaseBySupplier, sendPurchaseToSupplier } from 'interfaces/purchases';
import { toast } from 'react-toastify';
import { MdEmail, MdOpenInNew } from 'react-icons/md';
import logoToola from 'assets/img/logo/toola.png';


const PurchasePageSupplier = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);
  const isAuthenticated = Boolean(localStorage.getItem('token'));
  const marketingUrl = 'https://www.toola.fr/contact';
  const marketingUrlSite = 'https://toola.fr';


  const toNumber = (v: any, d = 0) => {
   const n = Number(v);
   return Number.isFinite(n) ? n : d;
 };
 const formatCurrency = (n: number) =>
   Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(toNumber(n));
 const getQty = (it: any) => toNumber(it?.quantity ?? it?.qty ?? 0);
 const getUnitPrice = (it: any) =>
   toNumber(it?.unitPrice ?? it?.price ?? it?.productId?.purchasePrice ?? 0);
 // Récupère le taux de TVA depuis l’item (priorité à item.vat), fallback produit.vatPurchase/vat
 const getVatRate = (it: any) => {
   const raw =
     it?.vat ??
     it?.productId?.vatPurchase ??
     it?.productId?.vat ??
     it?.vatPurchase ??
     0;
   const n = Number(raw);
   return Number.isFinite(n) ? n : 0;
 };

 const totals = useMemo(() => {
   const items = Array.isArray(po?.items) ? po.items : [];
   let ht = 0;
   let tva = 0;
   items.forEach((it: any) => {
     const q = getQty(it);
     const pu = getUnitPrice(it);
     const rate = getVatRate(it) / 100;
     const lineHT = q * pu;
      const lineTVA = lineHT * (rate > 0 ? rate : 0);
     ht += lineHT;
     tva += lineTVA;
   });
   return { totalLinesHT: ht, totalTVA: tva, totalTTC: ht + tva };
 }, [po?.items]);

 const formatPurchaseDate = (v: any) => {
       if (!v) return 'N/A';
       const dt = new Date(v);
       if (isNaN(dt.getTime())) return 'N/A';
       return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(dt);
     };

     const handlePrint = () => {
        window.print();
      };

  const getMagicToken = (po: any) => {
    if (po.magicLinkUuid) return po.magicLinkUuid;
    const url = po.magicLinkUuid || '';
    if (!url) return null;
    const parts = String(url).split('/');
    return parts[parts.length - 1] || null;
  };
  
  const handleSendEmail = async () => {
    const tokenToUse = token || getMagicToken(po);
    if (!tokenToUse) {
      toast.error('Aucun magic link disponible pour cet achat.');
      return;
    }
    setSendingEmailId(po?._id);
    const res = await sendPurchaseToSupplier(tokenToUse);
    setSendingEmailId(null);
    if (res && !res.error) toast.success('Email envoyé au fournisseur.');
    else toast.error('Erreur lors de l’envoi de l’email.');
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await getPurchaseBySupplierToken(token || '');
      if (!res) {
        toast.error('Lien invalide');
        setLoading(false);
        return;
      }
      setPo(res);
      setLoading(false);
    };
    load();
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    const r = await acceptPurchaseBySupplier(token);
    setAccepting(false);
    if (!r || r.error) return toast.error('Erreur');
    toast.success('Commande acceptée');
  };

  if (loading) {
    return (
      <div className="p-6">
        <Card extra="p-6">
          <p className="text-sm text-gray-500">Chargement…</p>
        </Card>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="p-6">
        <Card extra="p-6">
          <p className="text-sm text-red-500">Commande introuvable.</p>
        </Card>
      </div>
    );
  }

   const supplierAccepted = Boolean(po?.aceptedBySupplier);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
       {/* Colonne gauche: actions */}
       <div className="md:col-span-1">
         <Card extra="p-4 print-hide">
           <h3 className="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
           <button
                onClick={handlePrint}
                className="w-full mt-2 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 mb-10"
            >
                🖨️ Imprimer / PDF
           </button>

           <h3 className="text-sm font-semibold text-gray-700 mb-3">Votre acheteur</h3>
                <p className="text-sm text-gray-700 mb-1">
                    <span className="font-semibold">Contact :</span>{' '}
                    {po?.createdBy?.fullName || 'N/A'}
                </p>
                <p className="text-sm text-gray-700 mb-1">
                    <span className="font-semibold">Société :</span>{' '}
                    {po?.ownerInfo?.company || 'N/A'}
                </p>
                <p className="text-sm text-gray-700 mb-10">
                    <span className="font-semibold">Email :</span>{' '}
                    {po?.createdBy?.email || 'N/A'}
                </p>

           {isAuthenticated ? (
             supplierAccepted ? (
               <div className="text-xs font-medium text-emerald-600">Commande acceptée</div>
             ) : (
               <button
                 onClick={handleSendEmail}
                 disabled={!!sendingEmailId}
                 className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-50"
                 title="Envoyer la commande par email au fournisseur"
               >
                 <MdEmail className="w-4 h-4 text-toola-500" />
                 {sendingEmailId ? 'Envoi…' : 'Envoyer par email'}
               </button>
             )
           ) : (
             <>
                <a
                 href={marketingUrl}
                 target="_blank"
                 rel="noopener noreferrer"
                 className="mt-4 px-5 py-2.5 bg-toola-500 hover:bg-toola-600 text-white font-medium text-sm rounded-xl shadow-sm transition-all duration-200 text-center"
                 title="Se connecter au logiciel"
               >
                 <button className="w-4 h-4" />
                 Connectez-vous à votre logiciel !
               </a>
               <div className="mt-3 flex items-center justify-center gap-2 text-[12px] text-gray-500 mt-6">
                 <span>Automatisé par</span>
                 <a href={marketingUrlSite} target="_blank" rel="noopener noreferrer" className="inline-flex items-center">
                   <span className="ml-2 font-bold text-toola-500">TOOLA</span>
                 </a>
               </div>
            </>
           )}
         </Card>
       </div>

       {/* Colonne droite: contenu */}
       <div className="md:col-span-3">
         <Card extra="p-6">
           <h1 className="text-xl font-semibold text-gray-900 mb-2">
             Demande de commande #{po.purchaseId}
           </h1>
            <p className="text-sm text-gray-600 mb-6">
             Merci de confirmer la réception de cette demande.
           </p>
           <p className="text-sm text-gray-700 mb-1 mb-10">
             <span className="font-semibold">Date de la commande :</span>{' '}
             {formatPurchaseDate(po?.purchaseDate)}
           </p>
           <p className="text-sm text-gray-700 mb-1">
             <span className="font-semibold">Société :</span>{' '}
             {po?.ownerInfo?.company || 'N/A'}
           </p>
           <p className="text-sm text-gray-700 mb-1">
             <span className="font-semibold">Adresse de livraison :</span>{' '}
             {po?.ownerInfo?.address || 'N/A'}
           </p>
           <p className="text-sm text-gray-700 mb-10">
             <span className="font-semibold">Contact :</span>{' '}
             {po?.createdBy?.fullName || 'N/A'}
           </p>

           <div className="mb-6">
             <h2 className="text-sm font-semibold text-gray-700 mb-2">Articles</h2>
             <div className="overflow-x-auto border rounded-lg">
               <table className="w-full text-xs">
                 <thead className="bg-gray-50 text-gray-600">
                   <tr>
                     <th className="text-left py-2 px-3">Produit</th>
                     <th className="text-right py-2 px-3">Quantité</th>
                     <th className="text-right py-2 px-3">Prix unitaire (HT)</th>
                     <th className="text-right py-2 px-3">TVA (%)</th>
                     <th className="text-right py-2 px-3">Total HT</th>
                     <th className="text-right py-2 px-3">TVA (€)</th>
                     <th className="text-right py-2 px-3">Total TTC</th>
                   </tr>
                 </thead>
                 <tbody>
                   {(po.items || []).map((it: any, i: number) => {
                     const q = getQty(it);
                     const pu = getUnitPrice(it);
                     const rate = getVatRate(it);
                     const lineHT = q * pu;
                     const lineTVA = lineHT * (rate > 0 ? rate / 100 : 0);
                     const lineTTC = lineHT + lineTVA;
                     return (
                     <tr key={i} className="border-t border-gray-100">
                       <td className="py-2 px-3">
                         {it?.productId?.name || 'Produit'}
                       </td>
                       <td className="py-2 px-3 text-right">{q}</td>
                         <td className="py-2 px-3 text-right">{formatCurrency(pu)}</td>
                         <td className="py-2 px-3 text-right">{toNumber(rate)}%</td>
                         <td className="py-2 px-3 text-right">{formatCurrency(lineHT)}</td>
                         <td className="py-2 px-3 text-right">{formatCurrency(lineTVA)}</td>
                         <td className="py-2 px-3 text-right font-medium">{formatCurrency(lineTTC)}</td>
                     </tr>
                     )
                   })}
                   {(!po.items || po.items.length === 0) && (
                     <tr>
                       <td colSpan={8} className="py-4 px-3 text-center text-gray-400">
                          Aucun article
                        </td>
                     </tr>
                   )}
                 </tbody>
               </table>
               </div>
           </div>

           {Array.isArray(po.items) && po.items.length > 0 && (
             <div className="flex justify-end mt-4">
               <div className="text-sm text-navy-700 flex flex-col items-end gap-0.5">
                 <div className="flex gap-4">
                   <span className="text-gray-500">Total HT</span>
                   <span className="font-medium">{formatCurrency(totals.totalLinesHT)}</span>
                 </div>
                 <div className="flex gap-4 border-t pt-1 mt-1">
                   <span className="font-semibold">Total TTC</span>
                   <span className="font-semibold">{formatCurrency(totals.totalTTC)}</span>
                 </div>
               </div>
             </div>
           )}

           <div className="flex justify-end mt-6">
             {!isAuthenticated && !supplierAccepted && (
               <button
                 disabled={accepting}
                 onClick={handleAccept}
                 className="px-5 py-2 rounded-lg bg-toola-500 hover:bg-toola-600 text-white text-sm disabled:opacity-50"
               >
                 {accepting ? 'Validation…' : 'Accepter la commande'}
               </button>
             )}
             {!isAuthenticated && supplierAccepted && (
               <div className="text-xs font-medium text-emerald-600">
                 Commande acceptée
               </div>
             )}
           </div>
         </Card>
       </div>
     </div>
   </div>
  );
};

export default PurchasePageSupplier;
